import { useCallback } from 'react'
import { atom, useAtom } from 'jotai'
import useClientS3Upload from '../../../../../hooks/useClientS3Upload'
import useHandleSteps from './useHandleSteps'
import useStepError from './useStepError'
import useCloneRequestController from './useCloneRequestController'
import { getUrlFormValues } from './useUrlFormValues'
import { setProposal } from './useProposal'
import { setProposalResult } from './useProposalResult'

const DEFAULT_STEP = 0

export const stepAtom = atom(DEFAULT_STEP)

/** CORS Unblock 目前是否啟用（由 Tips 心跳更新：true 已開啟, false 未開啟, null 檢測中） */
export const corsStatusAtom = atom(null)

const defaultLastRun = { step: DEFAULT_STEP, loading: false, success: true }
export const lastRunAtom = atom(defaultLastRun)

/**
 * Step index only. Use when component needs only currentStep.
 */
export const useStepIndex = () => {
  return useAtom(stepAtom)
}

/**
 * Run state derived from lastRun: loadingStep, successStep.
 * Use when component needs loading/success without step actions.
 */
export const useStepRunState = () => {
  const [lastRun] = useAtom(lastRunAtom)
  return {
    loadingStep: lastRun.loading,
    successStep: lastRun.success
  }
}

/**
 * Step actions: nextStep, resetSteps, retryStep.
 * Use when component needs only actions (still subscribes to stepAtom for currentStep in closures).
 */
export const useStepActions = () => {
  const runStep = useHandleSteps()
  const [, setStepError] = useStepError()
  const { abortAndReset } = useCloneRequestController()
  const { reset: resetS3Upload } = useClientS3Upload()
  const [currentStep, setCurrentStep] = useAtom(stepAtom)
  const [, setLastRun] = useAtom(lastRunAtom)

  const updateCurrentStep = useCallback(
    async (step) => {
      setCurrentStep(step)
      setLastRun({ step, loading: true, success: true })

      const inputs = {
        urlFormValues: getUrlFormValues(),
        setProposal,
        setProposalResult,
        setStepError
      }
      const { success, step: nextStep } = await runStep(step, inputs)

      setLastRun({
        step,
        loading: false,
        success: success || nextStep === DEFAULT_STEP
      })

      if (!success || nextStep < 1) {
        return
      }
      return updateCurrentStep(nextStep)
    },
    [runStep, setStepError, setCurrentStep, setLastRun]
  )

  const nextStep = useCallback(() => {
    return updateCurrentStep(currentStep + 1)
  }, [currentStep, updateCurrentStep])

  const resetSteps = useCallback(() => {
    resetS3Upload()
    abortAndReset()
    setProposal(null)
    setProposalResult(null)
    setStepError(null)
    return updateCurrentStep(DEFAULT_STEP)
  }, [abortAndReset, resetS3Upload, setStepError, updateCurrentStep])

  const retryStep = useCallback(() => {
    setStepError(null)
    return updateCurrentStep(currentStep)
  }, [currentStep, setStepError, updateCurrentStep])

  return { nextStep, resetSteps, retryStep }
}

/**
 * Combined hook for convenience. Prefer useStepIndex / useStepRunState / useStepActions for targeted subscriptions.
 */
const useSteps = () => {
  const [currentStep] = useStepIndex()
  const { loadingStep, successStep } = useStepRunState()
  const { nextStep, resetSteps, retryStep } = useStepActions()

  return {
    currentStep,
    loadingStep,
    successStep,
    nextStep,
    resetSteps,
    retryStep
  }
}

export default useSteps
